<template>
  <div class="flex h-full min-h-0 w-full flex-col items-center justify-center p-8">
    <div
      ref="logoTarget"
      class="relative touch-none select-none"
      @touchstart.passive="handleTouchStart"
      @touchmove.passive="handleTouchMove"
      @touchend.passive="handleTouchEnd"
      @touchcancel.passive="handleTouchEnd"
    >
      <img src="@/assets/carvia_logo.svg" alt="Carvia" class="w-48 max-w-[60vw]" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onKeyStroke } from '@vueuse/core';
import { useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { Alert } from '@/services/alert.service';
import { useAlertStore } from '@/stores/alert.store';
import { useTwoFingerLongPress } from '@/hooks/use-two-finger-long-press';
import { useLogout } from '@/queries/auth/use-logout';

const { t } = useI18n();
const alertStore = useAlertStore();
const logoTarget = useTemplateRef<HTMLElement>('logoTarget');

const { mutateAsync: logout } = useLogout();

const { handleTouchStart, handleTouchMove, handleTouchEnd } = useTwoFingerLongPress(
  logoTarget,
  () => {
    void showDisconnectPrompt();
  },
);

async function showDisconnectPrompt() {
  if (alertStore.isVisible) return;

  const { isConfirmed } = await Alert.fire({
    titleText: t('disconnectTitle'),
    text: t('disconnectDescription'),
    icon: 'warning',
    allowEscapeKey: true,
    buttons: [
      {
        text: t('disconnect'),
        style: 'danger',
        propertyName: 'isConfirmed',
      },
      {
        text: t('cancel'),
        style: 'ghost',
        propertyName: 'isDismissed',
      },
    ],
  });

  if (isConfirmed) {
    await logout();
  }
}

onKeyStroke(
  'Escape',
  (event) => {
    if (alertStore.isVisible) return;
    event.preventDefault();
    void showDisconnectPrompt();
  },
  { target: window },
);
</script>

<i18n lang="json">
{
  "en": {
    "disconnectTitle": "Disconnect device?",
    "disconnectDescription": "This device will be unpaired. You will need a new authorization code from Settings to connect it again.",
    "disconnect": "Disconnect",
    "cancel": "Cancel"
  },
  "de": {
    "disconnectTitle": "Gerät trennen?",
    "disconnectDescription": "Dieses Gerät wird entkoppelt. Sie benötigen einen neuen Autorisierungscode aus den Einstellungen, um es erneut zu verbinden.",
    "disconnect": "Trennen",
    "cancel": "Abbrechen"
  },
  "es": {
    "disconnectTitle": "¿Desconectar dispositivo?",
    "disconnectDescription": "Este dispositivo se desvinculará. Necesitará un nuevo código de autorización desde Configuración para volver a conectarlo.",
    "disconnect": "Desconectar",
    "cancel": "Cancelar"
  }
}
</i18n>
